import '@assets/styles/tiptap-preview.css';

import { Chat, PinnedChat } from '@src/api/shared/community/chatApiClient';
import { ChannelMemberUserTypeEnum } from '@src/enum/ChannelMemberUserTypeEnum';
import ChatActionMenuPc from '@src/features/shared/community/components/chat/ChatActionMenuPc';
import ChatActionMenuSp from '@src/features/shared/community/components/chat/ChatActionMenuSp';
import ChatContent from '@src/features/shared/community/components/chat/ChatContent';
import { useChatMessageDisplay } from '@src/features/shared/community/components/chat/hooks/useChatMessageDisplay';
import { LinkPreviewData } from '@src/features/shared/community/components/chat/PreviewOgp';
import React, { Fragment } from 'react';

import ChatItemHeader from './chatList/ChatItemHeader';
import Tooltip from './Tooltip';

export type ChatMenuPermission = {
  canShowKebabMenu: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canReport: boolean;
};

// Group related props for better organization
export type ChatContextData = {
  chat: Chat;
  channelCode: string;
  channelVisibility?: number;
  pinnedChat?: PinnedChat;
  linkPreviewCache: Record<string, LinkPreviewData | null>;
};

export type ChatUserState = {
  isLoggedIn: boolean;
  currentUserId?: number;
  currentUserType?: ChannelMemberUserTypeEnum;
};

export type ChatUIState = {
  editingChatId: number | null;
  isActionMenuSpOpen: boolean;
  currentChatIdSp: number | undefined;
};

export type ChatCallbacks = {
  onPinClick: (chat_id: number | undefined) => Promise<void>;
  onChatEditClick: (chat: Chat) => void;
  onChatDeleteClick: (chat: Chat) => void;
  onChatReportClick: (chat: Chat) => void;
  onPinnedChatMenuClose: () => void;
  onSetCurrentChatIdSp: (chatId: number) => void;
  onPinnedChatLongPress: () => void;
  onToggleLinkPreview: (chatId: number, hidePreview: boolean) => void;
};

type ChatMessageProps = {
  contextData: ChatContextData;
  userState: ChatUserState;
  uiState: ChatUIState;
  callbacks: ChatCallbacks;
  shouldShowUnreadLine: (chatId: number) => boolean;
  getChatMenuPermission: (chat: Chat) => ChatMenuPermission;
  chatRef: (el: HTMLDivElement | null) => void;
};

const ChatMessage: React.FC<ChatMessageProps> = ({
  contextData,
  userState,
  uiState,
  callbacks,
  shouldShowUnreadLine,
  getChatMenuPermission,
  chatRef,
}) => {
  const { chat, channelCode, channelVisibility, pinnedChat, linkPreviewCache } = contextData;
  const { isLoggedIn, currentUserId, currentUserType } = userState;
  const { editingChatId, isActionMenuSpOpen, currentChatIdSp } = uiState;
  const {
    onPinClick,
    onChatEditClick,
    onChatDeleteClick,
    onChatReportClick,
    onPinnedChatMenuClose,
    onSetCurrentChatIdSp,
    onPinnedChatLongPress,
    onToggleLinkPreview,
  } = callbacks;
  const {
    mentionClickedChatId,
    setMentionClickedChatId,
    isMobile,
    tooltipRef,
    showTooltip,
    displayTooltip,
    preview,
    attachments,
    htmlWithMentions,
    reactions,
    longPressBind,
    isHtmlEmpty,
  } = useChatMessageDisplay({
    chat,
    isLoggedIn,
    currentUserId,
    currentUserType,
    linkPreviewCache,
    onSetCurrentChatIdSp,
    onPinnedChatLongPress,
  });

  const isOwner =
    isLoggedIn && chat.user_id === currentUserId && chat.user_type === currentUserType;

  return (
    <Fragment key={chat.id}>
      {shouldShowUnreadLine(chat.id) && (
        <div className='relative h-px bg-transparent py-[20px]'>
          <div className='absolute left-1/2 top-1/2 h-px w-full -translate-x-1/2 -translate-y-1/2 bg-warning-500'></div>
          <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-[10px] text-sm font-medium text-warning-500'>
            NEW
          </div>
        </div>
      )}

      <div
        ref={chatRef}
        className={`group relative flex flex-col gap-4 px-6 py-3 transition duration-300 ${
          editingChatId === chat.id
            ? 'mt-[30px] border border-r-0 border-[#D9D9D9] bg-[#f9f9f9]'
            : ''
        } ${pinnedChat?.id === chat.id ? 'bg-[#FCFBE8]' : 'hover:bg-gray-10 lg:hover:bg-gray-100'} ${
          isMobile ? 'select-none' : ''
        }`}
        {...longPressBind(chat.id)}
      >
        {editingChatId === chat.id && (
          <div className='absolute -left-px top-[-31px] grid h-[30px] w-[160px] place-items-center bg-[#D9D9D9] text-sm'>
            メッセージ編集中
          </div>
        )}
        <ChatItemHeader
          channelCode={channelCode}
          chat={chat}
          channelVisibility={channelVisibility}
          pinnedChat={pinnedChat}
          isLoggedIn={isLoggedIn}
        />
        <ChatContent
          chat={chat}
          htmlWithMentions={htmlWithMentions}
          preview={preview}
          attachments={attachments}
          reactions={reactions}
          setMentionClickedChatId={setMentionClickedChatId}
          displayTooltip={displayTooltip}
          isHtmlEmpty={isHtmlEmpty}
          isOwner={isOwner}
          onTogglePreview={(hidePreview) => onToggleLinkPreview(chat.id, hidePreview)}
        />
        <div className='absolute right-6 top-1 hidden group-hover:block lg:!hidden'>
          <ChatActionMenuPc
            chatId={chat.id}
            handlePinClick={() => onPinClick(pinnedChat?.id === chat.id ? undefined : chat.id)}
            handleChatEditClick={() => onChatEditClick(chat)}
            handleChatDeleteClick={() => onChatDeleteClick(chat)}
            handleChatReportClick={() => onChatReportClick(chat)}
            isPinned={pinnedChat?.id === chat.id}
            chatMenuPermission={getChatMenuPermission(chat)}
            currentReactions={reactions}
          />
        </div>
        {isActionMenuSpOpen && currentChatIdSp === chat.id && (
          <ChatActionMenuSp
            chatId={currentChatIdSp}
            handleCloseClick={onPinnedChatMenuClose}
            handleChatLinkCopyClick={() => console.log('link copied')}
            handlePinClick={() =>
              onPinClick(pinnedChat?.id === currentChatIdSp ? undefined : currentChatIdSp)
            }
            handleChatEditClick={() => onChatEditClick(chat)}
            handleChatDeleteClick={() => onChatDeleteClick(chat)}
            handleChatReportClick={() => onChatReportClick(chat)}
            isPinned={pinnedChat?.id === chat.id}
            chatMenuPermission={getChatMenuPermission(chat)}
            isLoggedIn={isLoggedIn}
            currentReactions={reactions}
          />
        )}
        {showTooltip && mentionClickedChatId === chat.id && (
          <div
            ref={tooltipRef}
            className='absolute left-1/2 top-1/2 z-[999] -translate-x-1/2 -translate-y-1/2 text-[12px] lg:left-[40%] lg:ml-[17%] lg:w-[290px]'
          >
            <Tooltip
              label='表示するには、ログインしてください。'
              weight='500'
            />
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default ChatMessage;
